import React from 'react';
import { checkReportReadiness } from '../utils/microbiologyTemplateEngine';

export default function DoctorReviewModeSection({
  data,
  onReleaseReport,
  onOpenReportModal
}) {
  if (!data) return null;

  const readiness = checkReportReadiness(data);

  return (
    <div className="wb-stage-view">
      {/* Header */}
      <div className="wb-stage-header">
        <div className="wb-stage-title-wrap">
          <h3 className="wb-stage-title">Microbiologist Review & Report Validation Gate</h3>
          <span className="wb-stage-subtitle">
            Senior clinical review: verify diagnostic milestones, inspect AST interpretations, and authorize final release.
          </span>
        </div>

        <button
          type="button"
          className="wb-btn-secondary-sm"
          onClick={onOpenReportModal}
        >
          🖨️ Preview Official Report
        </button>
      </div>

      {/* 1. Final Report Readiness Gate */}
      <div className="wb-card-clean" style={{ marginBottom: '14px' }}>
        <div className="wb-card-clean-header">
          <div>
            <h4 className="wb-card-clean-title" style={{ fontSize: '0.88rem', letterSpacing: '0.03em' }}>
              FINAL REPORT READINESS
            </h4>
            <span className="wb-hint-text">
              Mandatory clinical verification checklist prior to final authorization and legal release.
            </span>
          </div>
          <span
            className={`wb-gate-badge ${readiness.isReadyForFinal ? 'ready' : 'blocked'}`}
          >
            {readiness.isReadyForFinal ? '✓ Ready for Release' : '⚠ Action Required'}
          </span>
        </div>

        <div className="wb-checklist-grid">
          {readiness.checklist.map((item) => (
            <div key={item.id} className={`wb-checklist-cell ${item.status}`}>
              <div className="wb-checklist-status-icon">
                {item.status === 'pass' ? (
                  <span style={{ color: '#15803d' }}>✓</span>
                ) : (
                  <span style={{ color: '#dc2626' }}>⚠</span>
                )}
              </div>
              <div className="wb-checklist-content">
                <strong className="wb-checklist-label">{item.label}</strong>
                <span className="wb-checklist-detail">{item.detail}</span>
              </div>
            </div>
          ))}
        </div>

        {!readiness.isReadyForFinal && (
          <div className="wb-callout-danger" style={{ marginTop: '10px' }}>
            <strong>⚠ Mandatory Requirements Incomplete:</strong>
            <ul style={{ margin: '4px 0 0 16px', padding: 0 }}>
              {readiness.blockingReasons.map((reason, idx) => (
                <li key={idx}>{reason}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      {/* 2. Executive Case Summary */}
      <div className="wb-card-clean" style={{ marginBottom: '16px' }}>
        <div className="wb-card-clean-header">
          <h4 className="wb-card-clean-title">Executive Case Summary</h4>
        </div>

        <div className="wb-summary-metrics-strip">
          <div className="wb-metric-cell">
            <span className="wb-metric-label">Specimen</span>
            <strong className="wb-metric-value">{data.specimenName}</strong>
          </div>
          <div className="wb-metric-cell">
            <span className="wb-metric-label">Culture Finding</span>
            <strong className="wb-metric-value" style={{ color: data.cultureStatus === 'Growth' ? '#b91c1c' : '#15803d' }}>
              {data.cultureStatus}
            </strong>
          </div>
          <div className="wb-metric-cell">
            <span className="wb-metric-label">Colony Count</span>
            <strong className="wb-metric-value">{data.colonyCount || 'Pending'}</strong>
          </div>
          <div className="wb-metric-cell">
            <span className="wb-metric-label">Total Isolates</span>
            <strong className="wb-metric-value">{data.isolates?.length || 0}</strong>
          </div>
        </div>

        {/* Isolates and AST Breakdown List */}
        {data.isolates?.length > 0 && (
          <div className="wb-exec-isolates-list">
            {data.isolates.map((iso, idx) => {
              const sList = (iso.astResults || []).filter((r) => r.finalInterpretation === 'S');
              const iList = (iso.astResults || []).filter((r) => r.finalInterpretation === 'I' || r.finalInterpretation === 'SDD');
              const rList = (iso.astResults || []).filter((r) => r.finalInterpretation === 'R');

              return (
                <div key={iso.isolateID || idx} className="wb-exec-isolate-row">
                  <div className="wb-exec-isolate-header">
                    <div>
                      <span className="wb-isolate-badge">#{iso.isolateNumber || idx + 1}</span>
                      <strong style={{ fontSize: '0.95rem', color: '#0f172a', marginLeft: '6px' }}>
                        {iso.organismName}
                      </strong>
                      <span className="wb-tag-gram" style={{ marginLeft: '8px' }}>
                        {iso.gramType}
                      </span>
                    </div>
                    <span className="wb-hint-text">
                      Method: {iso.identificationMethod || 'Automated VITEK-2'}
                    </span>
                  </div>

                  <div className="wb-exec-ast-lines">
                    <div className="wb-exec-ast-line">
                      <strong style={{ color: '#15803d', minWidth: '130px' }}>
                        ✓ Susceptible ({sList.length}):
                      </strong>
                      <span>{sList.map((d) => d.drugName).join(', ') || 'None'}</span>
                    </div>

                    {iList.length > 0 && (
                      <div className="wb-exec-ast-line">
                        <strong style={{ color: '#b45309', minWidth: '130px' }}>
                          ⚡ Intermediate ({iList.length}):
                        </strong>
                        <span>{iList.map((d) => d.drugName).join(', ')}</span>
                      </div>
                    )}

                    <div className="wb-exec-ast-line">
                      <strong style={{ color: '#dc2626', minWidth: '130px' }}>
                        🚨 Resistant ({rList.length}):
                      </strong>
                      <span style={{ color: '#b91c1c', fontWeight: '600' }}>
                        {rList.map((d) => d.drugName).join(', ') || 'None'}
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 3. Post-Release Immutable Audit Stamp */}
      {data.approved && (
        <div className="wb-callout-success" style={{ marginBottom: '16px' }}>
          <span className="wb-callout-icon">✓</span>
          <div>
            <strong>Report Successfully Authorized & Released</strong>
            <p>
              Released by <strong>{data.approvedBy || 'Authorized Microbiologist'}</strong> on{' '}
              {data.approvedAt ? new Date(data.approvedAt).toLocaleString() : new Date().toLocaleString()}. Legal immutable snapshot frozen (SHA-256 Verified).
            </p>
          </div>
        </div>
      )}

      {/* 4. Release Actions */}
      <div className="wb-card-clean">
        <div className="wb-card-clean-header">
          <div>
            <h4 className="wb-card-clean-title">Report Release Actions</h4>
            <span className="wb-hint-text">
              Authorize diagnostic findings and publish the official diagnostic report to clinical wards.
            </span>
          </div>
        </div>

        <div className="wb-release-btn-group">
          <button
            type="button"
            className="wb-btn-secondary"
            onClick={() => onReleaseReport('Preliminary')}
          >
            Issue Preliminary Report
          </button>

          <button
            type="button"
            className="wb-btn-release"
            disabled={!readiness.isReadyForFinal}
            onClick={() => onReleaseReport('Final')}
          >
            ✓ Approve & Release Final Report
          </button>

          {data.approved && (
            <button
              type="button"
              className="wb-btn-text-danger"
              onClick={() => onReleaseReport('Amended')}
            >
              Issue Clinical Amendment
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
